import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import {
  getInvoiceConfigurationThunk,
  postInvoiceConfigurationThunk,
} from "store/reducers/invoice/invoiceConfigSlice";
import * as yup from "yup";

const validation = yup.object({
  address: yup.string().required(),
  GSTIN: yup.string().required(),
  phone: yup.string().required().length(10),
  email: yup.string().email().required(),
  web: yup.string().required(),
});

const InvoiceConfig = () => {
  useEffect(() => {
    dispatch(getInvoiceConfigurationThunk());
  }, []);

  const state = useSelector((state) => state.invoiceConfig);
  const formik = useFormik({
    initialValues: {
      address: state.data?.address ?? "",
      GSTIN: state.data?.GSTIN ?? "",
      phone: state.data?.phone?.slice(3) ?? "",
      email: state.data?.email ?? "",
      web: state.data?.web ?? "",
      logo: "",
    },
    validationSchema: validation,

    onSubmit: (data) => {
      const _data = { ...data, phone: `+91${data.phone}` };
      Object.keys(_data).forEach((key) => {
        if (_data[key] === "") {
          delete _data[key];
        }
      });
      dispatch(postInvoiceConfigurationThunk(_data));
    },
    validateOnChange: false,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (state.errorMsg) {
      Object.keys(state.errorMsg).forEach((key) => {
        formik.setFieldError(key, state.errorMsg[key][0]);
      });
    }
  }, [state]);

  return (
    <Box padding={2}>
      <Grid container columnSpacing={2} rowSpacing={0}>
        <Grid item xs={12} md={6} lg={4}>
          <Typography color="text.secondary" mb={2} variant="h5">
            {state.noData
              ? "Please add invoice configuration"
              : "Do you want to update invoice configuration?"}
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              label="Company Address"
              variant="outlined"
              name="address"
              fullWidth
              value={formik.values.address}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.address)}
              helperText={formik.errors.address}
              autoComplete="new-password"
              type="text"
              required
              multiline
              rows={4}
            />
            <TextField
              label="GSTIN"
              variant="outlined"
              name="GSTIN"
              fullWidth
              sx={{ mt: 2 }}
              value={formik.values.GSTIN}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.GSTIN)}
              helperText={formik.errors.GSTIN}
              autoComplete="new-password"
              type="text"
              required
            />
            <TextField
              label="Contact Phone"
              name="phone"
              type="phone"
              autoComplete="new-password"
              fullWidth
              sx={{ mt: 2 }}
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.phone)}
              helperText={formik.errors.phone}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                ),
              }}
            />
            <TextField
              label="Contact Email"
              variant="outlined"
              name="email"
              fullWidth
              type="email"
              sx={{ mt: 2 }}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.email)}
              helperText={formik.errors.email}
              autoComplete="new-password"
              required
            />
            <TextField
              label="Company Web address"
              variant="outlined"
              name="web"
              fullWidth
              type="url"
              sx={{ mt: 2 }}
              value={formik.values.web}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.web)}
              helperText={formik.errors.web}
              autoComplete="new-password"
              required
            />
            {state.data?.logo && (
              <img
                src={state.data?.logo}
                alt="logo"
                style={{ marginTop: 2, marginBottom: 2 }}
              />
            )}
            <TextField
              label="Logo"
              variant="outlined"
              name="logo"
              fullWidth
              onChange={formik.handleChange}
              error={Boolean(formik.errors.logo)}
              helperText={formik.errors.logo}
              autoComplete="off"
              type="file"
              sx={{ mt: 2 }}
              required={state.noData}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputProps: { accept: "image/*" },
                onChange: (event) => {
                  formik.setFieldValue("logo", event.currentTarget.files[0]);
                },
              }}
            />
            {state.success && (
              <Alert sx={{ my: 1 }} severity="success">
                Success
              </Alert>
            )}

            {state.error && (
              <Alert sx={{ my: 1 }} severity="error">
                Error occurred
              </Alert>
            )}
            <LoadingButton
              variant="contained"
              type="submit"
              sx={{ paddingX: 10, marginTop: 2 }}
              loading={state?.loading}
            >
              {state.noData ? "Add" : "Update"}
            </LoadingButton>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceConfig;
